import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import VisitSectionStatus from './VisitSectionStatus';
import { WORKSPACE_SECTIONS } from './VisitWorkspaceTabs';
import http from '../../services/http';

function safeJsonParse(value) {
  try {
    return JSON.parse(value || 'null');
  } catch {
    return null;
  }
}

function readStoredUser() {
  const keys = ['loggedInUser', 'user', 'currentUser', 'authUser', 'cmfmUser', 'portalUser'];
  const stores = [];
  try { stores.push(localStorage); } catch {}
  try { stores.push(sessionStorage); } catch {}

  for (const store of stores) {
    for (const key of keys) {
      try {
        const raw = safeJsonParse(store.getItem(key));
        if (!raw) continue;
        return raw.user || raw;
      } catch {}
    }
  }
  return {};
}

function normalizeRole(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');
}

const SELF_COMPLETE_ROLES = new Set([
  'junior resident',
  'senior resident',
  'faculty',
  'admin',
  'hod',
  'medical officer',
  'cmfm',
  'ayush',
  'ayush medical officer',
]);

async function submitVisitLifecycle(visitId) {
  const body = { workspaceCompletion: true };
  try {
    return await http.patch(`/api/visits/${encodeURIComponent(visitId)}/prescription/submit`, body);
  } catch (error) {
    const status = Number(error?.response?.status || 0);
    if (status && ![404, 405].includes(status)) throw error;
    return http.post(`/api/visit-prescription/${encodeURIComponent(visitId)}/submit`, body);
  }
}

export default function VisitCompletionPanel({
  statuses = {},
  visitId = '',
  visit = null,
  onCompleted,
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const user = useMemo(() => readStoredUser(), []);
  const role = normalizeRole(user?.role || user?.userType || user?.type);
  const isIntern = role === 'intern';
  const canSelfComplete = SELF_COMPLETE_ROLES.has(role);
  const visitStatus = normalizeRole(visit?.prescription?.status || visit?.status || 'draft');

  const incompleteSections = useMemo(
    () => WORKSPACE_SECTIONS.filter((section) => statuses[section.key] !== 'saved'),
    [statuses]
  );
  const allSaved = incompleteSections.length === 0;
  const awaitingReview = ['submitted', 'pending'].includes(visitStatus);
  const alreadyApproved = visitStatus === 'approved';
  const returned = visitStatus === 'returned';

  const handleComplete = async () => {
    if (!visitId || busy || !allSaved || awaitingReview || alreadyApproved) return;
    if (!isIntern && !canSelfComplete) {
      setError('Your current role is not configured for Visit completion.');
      return;
    }

    setBusy(true);
    setError('');
    setSuccess('');
    try {
      const response = await submitVisitLifecycle(visitId);
      const payload = response?.data || response || {};
      const assignedName = payload?.assignedTo?.name || payload?.assignment?.reviewer?.name || '';
      const resultingStatus = normalizeRole(payload?.currentStatus || payload?.status);

      if (isIntern) {
        setSuccess(
          assignedName
            ? `Sent for approval and assigned to ${assignedName}.`
            : 'Sent for approval. No eligible reviewer is currently assigned; the Visit remains available in the centre review queue.'
        );
      } else if (resultingStatus === 'approved') {
        setSuccess('Visit completed and self-approved.');
      } else {
        setSuccess('Visit completion saved.');
      }

      if (typeof onCompleted === 'function') {
        await onCompleted(payload);
      }
    } catch (e) {
      const data = e?.response?.data || {};
      const missing = Array.isArray(data?.missingSections) ? data.missingSections : [];
      const suffix = missing.length ? ` Missing: ${missing.join(', ')}.` : '';
      setError(`${data?.message || e?.message || 'Visit completion failed.'}${suffix}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
        Visit completion
      </Typography>

      <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' } }}>
        {WORKSPACE_SECTIONS.map((section) => (
          <Box key={section.key} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'center' }}>
            <Typography variant="body2">{section.label}</Typography>
            <VisitSectionStatus status={statuses[section.key]} />
          </Box>
        ))}
      </Box>

      {!allSaved && !awaitingReview && !alreadyApproved ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          Save all four clinical sections before {isIntern ? 'sending this Visit for approval' : 'completing this Visit'}.
          {incompleteSections.length ? ` Remaining: ${incompleteSections.map((section) => section.label).join(', ')}.` : ''}
        </Alert>
      ) : null}

      {awaitingReview ? (
        <Alert severity="warning" sx={{ mt: 2 }}>
          This Visit is awaiting reviewer action and is read-only for approval workflow decisions.
        </Alert>
      ) : null}

      {returned ? (
        <Alert severity="warning" sx={{ mt: 2 }}>
          This Visit was returned for editing. Save the corrected section(s), then resend it for approval.
        </Alert>
      ) : null}

      {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}
      {success ? <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert> : null}

      {!awaitingReview && !alreadyApproved ? (
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
          <Button
            variant="contained"
            onClick={handleComplete}
            disabled={!allSaved || busy || (!isIntern && !canSelfComplete)}
          >
            {busy ? (
              <Stack component="span" direction="row" spacing={1} alignItems="center">
                <CircularProgress size={16} color="inherit" />
                <span>{isIntern ? 'Sending…' : 'Completing…'}</span>
              </Stack>
            ) : isIntern ? (
              returned ? 'Resend for Approval' : 'Send for Approval'
            ) : (
              'Complete Visit'
            )}
          </Button>
        </Stack>
      ) : null}
    </Paper>
  );
}
